import type { Notification } from '@senviro/core-api-client';

/**
 * W22-F7-S1 — group notifications by entity bucket.
 *
 * Buckets are derived from `notification.entity_type` first (matches Core's
 * Phase 3 schema field), then from `notification.kind` (which carries
 * dotted module names like `task.assigned` / `evaluation.completed`), and
 * finally fall through to `Other` for anything we don't recognise.
 *
 * Order is deliberate (legacy parity from
 * `git show dd9b1eeb^:src/Template/Element/Metronic/menus/notifications.ctp`):
 *   Assets → Portfolios → Holders → Partners → Tasks → Invitations → Other.
 *
 * Within each bucket the spec says:
 *   - unread first (read_at == null)
 *   - then created_at desc
 */

export const BUCKET_ORDER = [
  'Asset',
  'Portfolio',
  'Holder',
  'Partner',
  'Task',
  'Invitation',
  'Other',
] as const;

export type BucketName = (typeof BUCKET_ORDER)[number];

/** Maps known entity_type / kind hints to a bucket. */
export function bucketFor(notification: Notification): BucketName {
  // 1. Direct entity_type match (case-insensitive, singular)
  const et = (notification.entity_type ?? '').toLowerCase();
  if (et) {
    if (et.startsWith('asset') || et === 'building') return 'Asset';
    if (et.startsWith('portfolio')) return 'Portfolio';
    if (et.startsWith('holder')) return 'Holder';
    if (et.startsWith('partner') || et === 'organization') return 'Partner';
    if (et.startsWith('task')) return 'Task';
    if (et.startsWith('invit')) return 'Invitation';
  }

  // 2. Kind prefix (e.g. `task.assigned`, `invitation.created`)
  const kind = (notification.kind ?? '').toLowerCase();
  if (kind.startsWith('asset.') || kind.startsWith('building.')) return 'Asset';
  if (kind.startsWith('portfolio.')) return 'Portfolio';
  if (kind.startsWith('holder.') || kind.startsWith('ownership.')) return 'Holder';
  if (kind.startsWith('partner.') || kind.startsWith('organization.')) return 'Partner';
  if (kind.startsWith('task.')) return 'Task';
  if (kind.startsWith('invitation.') || kind.startsWith('invite.')) return 'Invitation';

  return 'Other';
}

export interface GroupedNotifications {
  bucket: BucketName;
  items: Notification[];
  unreadCount: number;
}

/**
 * Project a flat list of notifications into the ordered, sorted, grouped
 * shape consumed by NotificationCenterPage. Empty buckets are excluded.
 */
export function groupByEntity(items: Notification[]): GroupedNotifications[] {
  const buckets = new Map<BucketName, Notification[]>();
  for (const bucket of BUCKET_ORDER) buckets.set(bucket, []);
  for (const n of items) {
    buckets.get(bucketFor(n))!.push(n);
  }

  const out: GroupedNotifications[] = [];
  for (const bucket of BUCKET_ORDER) {
    const list = buckets.get(bucket)!;
    if (list.length === 0) continue;
    const sorted = [...list].sort((a, b) => {
      const aUnread = a.read_at == null ? 1 : 0;
      const bUnread = b.read_at == null ? 1 : 0;
      if (aUnread !== bUnread) return bUnread - aUnread;
      const at = new Date(a.created_at).getTime();
      const bt = new Date(b.created_at).getTime();
      return bt - at;
    });
    const unreadCount = sorted.filter((n) => n.read_at == null).length;
    out.push({ bucket, items: sorted, unreadCount });
  }
  return out;
}
